import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus,
  Compass,
  Stamp,
  PencilRuler,
  HardHat,
  ShieldCheck,
  LandPlot,
  Split,
  Scale,
  Leaf,
  FileBadge,
  Receipt,
  Mail,
  MessageCircle,
} from "lucide-react";
import { Reveal } from "./Reveal";
import { useLang } from "@/i18n";

// Icons follow the fixed order of services.items (identical in DE & IT).
const SERVICE_ICONS = [
  Compass, // Vorstudie
  Stamp, // Einreichplanung
  PencilRuler, // Ausführungsplanung
  HardHat, // Bauleitung
  ShieldCheck, // Sicherheitskoordination
  LandPlot, // Gebäudekatastermeldungen
  Split, // Materielle Hausteilungspläne
  Scale, // Schätzungen
  Leaf, // Klimahausberechnungen
  FileBadge, // APE-Meldungen
  Receipt, // ENEA-Meldungen
];

const Row = ({ index, title, text, detail, inquiry, open, onToggle }) => {
  const [hover, setHover] = useState(false);
  const num = String.fromCharCode(97 + index) + ")";
  const Icon = SERVICE_ICONS[index] || Compass;
  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className="group relative border-b border-[var(--line)] first:border-t"
      data-testid={`service-row-${index}`}
    >
      <motion.div
        className="absolute inset-0 bg-[var(--bg-2)]"
        initial={false}
        animate={{ opacity: hover || open ? 1 : 0 }}
        transition={{ duration: 0.4 }}
      />
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        data-testid={`service-toggle-${index}`}
        className="relative w-full text-left flex items-baseline gap-4 md:gap-8 py-6 md:py-8 px-1 md:px-4 cursor-pointer"
      >
        <span className="font-mono2 text-xs md:text-sm text-[var(--txt-mut)] w-5 shrink-0 pt-1">
          {num}
        </span>
        <motion.span
          className="shrink-0 self-center"
          animate={{ color: hover || open ? "#d9cfb8" : "#6f6a5f" }}
          transition={{ duration: 0.4 }}
        >
          <Icon className="h-5 w-5 md:h-6 md:w-6" strokeWidth={1.3} />
        </motion.span>
        <div className="flex-1 min-w-0 flex flex-col md:flex-row md:items-baseline md:justify-between gap-2 md:gap-10">
          <motion.h3
            className="font-heading font-bold text-2xl md:text-4xl tracking-tight break-words hyphens-auto"
            animate={{ x: hover || open ? 14 : 0, color: hover || open ? "#d9cfb8" : "#f2f0eb" }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          >
            {title}
          </motion.h3>
          <p className="font-body font-light text-sm md:text-base text-[var(--txt-dim)] md:max-w-sm md:text-right">
            {text}
          </p>
        </div>
        <motion.span
          className="shrink-0 self-center text-[var(--accent)]"
          animate={{ rotate: open ? 135 : 0 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        >
          <Plus className="h-5 w-5" strokeWidth={1.4} />
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {open && detail && (
          <motion.div
            key="detail"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="relative overflow-hidden"
            data-testid={`service-detail-${index}`}
          >
            <div className="pl-[2.5rem] md:pl-[4.75rem] pr-6 md:pr-12 pb-7 md:pb-9 -mt-1 max-w-2xl">
              <p className="font-body font-light text-sm md:text-base text-[var(--txt-dim)] leading-relaxed">
                {detail}
              </p>
              {inquiry && (
                <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 font-mono2 text-[10px] tracking-[0.25em] uppercase text-[var(--txt-mut)]">
                  <span>{inquiry.label}</span>
                  <a
                    href={inquiry.mailHref}
                    data-testid={`service-inquire-email-${index}`}
                    className="inline-flex items-center gap-1.5 text-[var(--accent)] hover:text-white transition-colors duration-300 link-underline"
                  >
                    <Mail className="h-3.5 w-3.5" strokeWidth={1.6} />
                    {inquiry.email}
                  </a>
                  <a
                    href={inquiry.waHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`service-inquire-whatsapp-${index}`}
                    className="inline-flex items-center gap-1.5 text-[var(--txt-dim)] hover:text-[var(--accent)] transition-colors duration-300 link-underline"
                  >
                    <MessageCircle className="h-3.5 w-3.5" strokeWidth={1.6} />
                    {inquiry.whatsapp}
                  </a>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const Services = () => {
  const { t } = useLang();
  const s = t.services;
  const [openIndex, setOpenIndex] = useState(null);
  return (
    <section
      id="leistungen"
      className="relative border-t border-[var(--line)] bg-[var(--bg-1)] py-24 md:py-36"
      data-testid="services-section"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-14 md:mb-20 gap-6">
          <div>
            <Reveal>
              <span className="font-mono2 text-[11px] tracking-[0.4em] uppercase text-[var(--txt-mut)]">
                {s.label}
              </span>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-heading font-bold tracking-tight text-4xl md:text-5xl lg:text-6xl mt-6 leading-[0.95]">
                {s.heading}
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.2}>
            <p className="font-body font-light text-[var(--txt-dim)] text-base md:max-w-xs">
              {s.intro}
            </p>
          </Reveal>
        </div>

        <div data-testid="services-list">
          {s.items.map(([title, text, detail], i) => {
            const subject = `${s.inquireSubject}: ${title}`;
            const msg = s.inquireMsg.replace("{title}", title);
            const inquiry = {
              label: s.inquire,
              email: s.inquireEmail,
              whatsapp: s.inquireWhatsapp,
              mailHref: `mailto:arch.gasteiger@gmail.com?subject=${encodeURIComponent(
                subject,
              )}&body=${encodeURIComponent(msg)}`,
              waHref: `https://wa.me/393397966579?text=${encodeURIComponent(msg)}`,
            };
            return (
              <Reveal key={i} delay={Math.min(i * 0.05, 0.3)} y={20}>
                <Row
                  index={i}
                  title={title}
                  text={text}
                  detail={detail}
                  inquiry={inquiry}
                  open={openIndex === i}
                  onToggle={() => setOpenIndex((cur) => (cur === i ? null : i))}
                />
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};
